import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2, Phone, Star } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";

/* L'avis après le cours d'essai (l'école et Jen, 01.11.2026 : « très court, moins d'une
   minute »). On arrive ici depuis une étoile de l'e-mail : la note est déjà prise. Puis
   « Envie de continuer ? » (Oui → s'inscrire ; J'hésite / Non → pourquoi, en un toucher, avec
   un raccourci utile selon la raison). Chaque toucher s'enregistre tout de suite. */

const API = `${process.env.REACT_APP_BACKEND_URL}/api/essais-avis`;
const SUITES = [
  { cle: "oui", libelle: "Oui ! 🎉" },
  { cle: "hesite", libelle: "J'hésite encore" },
  { cle: "non", libelle: "Non" },
];
const MOTS = ["", "Bof", "Moyen", "Bien", "Très bien", "J'ai adoré !"];
const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const jourTexte = (iso) => {
  const [a, m, j] = String(iso || "").split("-").map(Number);
  if (!a || !m || !j) return "";
  return `${JOURS[new Date(a, m - 1, j).getDay()]} ${j} ${MOIS[m - 1]}`;
};
// Amène doucement la question suivante à l'écran (téléphone : elle est souvent plus bas).
const defiler = (ref) => setTimeout(() => ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" }), 150);

export default function AvisEssai() {
  useSEO({ title: "Ton avis — 1000 ARTS", description: "Ton avis sur ton cours d'essai chez 1000 ARTS.", canonical: "/avis-essai" });
  const [params] = useSearchParams();
  const jeton = params.get("j") || "";
  const noteLien = Number(params.get("note") || 0);
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [autre, setAutre] = useState("");
  const [mot, setMot] = useState("");
  const [lent, setLent] = useState(false);
  const premier = useRef(true);
  const refSuite = useRef(null);
  const refApres = useRef(null);

  const envoyer = useCallback(async (champs) => {
    setEnvoi(true);
    try {
      const r = await fetch(`${API}/${encodeURIComponent(jeton)}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(champs) });
      const x = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(x.detail || "Pas enregistré, réessaie.");
      setD(x); setErreur("");
      return true;
    } catch (e) { setErreur(e.message); return false; } finally { setEnvoi(false); }
  }, [jeton]);

  useEffect(() => {
    if (!jeton) { setErreur("Ce lien n'est pas complet."); return undefined; }
    // Le serveur peut mettre un moment à répondre (Jen, 01.11.2026 : la roue tournait sur le
    // serveur de test qui se réveillait) : après 4 s, un mot ; sans réponse, on réessaie
    // tout seul (5 fois, toutes les 5 s) avant de dire quoi faire.
    let fini = false;
    const minuterie = setTimeout(() => setLent(true), 4000);
    const charger = async (essai) => {
      try {
        const r = await fetch(`${API}/${encodeURIComponent(jeton)}`);
        if (r.status >= 500) throw new TypeError(`serveur ${r.status}`);
        const x = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(x.detail || "Ce lien n'est plus valable.");
        if (fini) return;
        setD(x); setAutre(x.autre || ""); setMot(x.commentaire || "");
        // L'étoile touchée dans l'e-mail : enregistrée dès l'arrivée.
        if (premier.current && noteLien >= 1 && noteLien <= 5 && x.note !== noteLien) envoyer({ note: noteLien });
        premier.current = false;
      } catch (e) {
        if (fini) return;
        if (e instanceof TypeError && essai < 5) { setTimeout(() => charger(essai + 1), 5000); return; }
        setErreur(e instanceof TypeError ? "La page ne se charge pas : vérifie ta connexion, puis touche encore une étoile dans l'e-mail." : e.message);
      }
    };
    charger(0);
    return () => { fini = true; clearTimeout(minuterie); };
  }, [jeton]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d) {
    return (
      <Cadre>
        {erreur ? <p className="text-center text-[15px]" data-testid="avis-erreur">{erreur}</p>
          : (
            <div className="py-16 grid place-items-center gap-3 text-center" data-testid="avis-chargement">
              <Loader2 className="animate-spin" />
              {lent && <p className="text-[15px] text-[var(--muted)]">Un instant, on prépare ta page…</p>}
            </div>
          )}
      </Cadre>
    );
  }
  const qui = d.adulte || !d.eleve_prenom ? "" : d.eleve_prenom;
  const raisons = new Set(d.raisons || []);
  const basculer = (cle) => {
    const n = new Set(raisons);
    if (n.has(cle)) n.delete(cle); else n.add(cle);
    envoyer({ raisons: [...n] });
  };
  const fini = d.termine;

  return (
    <Cadre>
      <div className="text-center">
        <div className="eyebrow mb-2">Cours d'essai{d.cours_nom ? ` · ${d.cours_nom}` : ""}</div>
        {jourTexte(d.date_essai) && <div className="text-sm text-[var(--muted)] -mt-1 mb-2">{jourTexte(d.date_essai)}</div>}
        <h1 className="font-display text-[30px] md:text-4xl leading-tight tracking-tight">
          {d.prenom ? `Merci ${d.prenom} !` : "Merci !"}
        </h1>
      </div>

      {/* 1. La note */}
      <section className="card-flat text-center" data-testid="avis-note">
        <div className="text-[17px] font-semibold">{qui ? `Comment s'est passé le cours de ${qui} ?` : "Comment s'est passé ton cours ?"}</div>
        <div className="flex justify-center gap-1.5 mt-3" role="radiogroup" aria-label="Note de 1 à 5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" onClick={async () => { if (await envoyer({ note: n })) defiler(refSuite); }}
              aria-label={`${n} sur 5 · ${MOTS[n]}`} aria-pressed={d.note === n}
              data-testid={`avis-etoile-${n}`} className="p-1.5 transition-transform active:scale-90">
              <Star size={38} strokeWidth={1.5} fill={d.note >= n ? "#F5A524" : "none"} color={d.note >= n ? "#F5A524" : "#CBD5E1"} />
            </button>
          ))}
        </div>
        <div className="text-sm mt-1 h-5 text-[var(--muted)]">{d.note ? MOTS[d.note] : "Touche une étoile"}</div>
      </section>

      {/* 2. Continuer ? */}
      {d.note ? (
        <section className="card-flat" data-testid="avis-suite" ref={refSuite}>
          <div className="text-[17px] font-semibold text-center">{qui ? `${qui} a envie de continuer chez 1000 ARTS ?` : "Envie de continuer chez 1000 ARTS ?"}</div>
          <div className="grid gap-2 mt-4">
            {SUITES.map((s) => (
              <button key={s.cle} type="button" aria-pressed={d.suite === s.cle}
                onClick={async () => { if (await envoyer({ suite: s.cle })) defiler(refApres); }} data-testid={`avis-suite-${s.cle}`}
                className="w-full rounded-2xl border px-4 py-3.5 text-[16px] font-semibold transition-colors"
                style={d.suite === s.cle ? { background: "var(--primary)", borderColor: "var(--primary)", color: "#04202A" }
                  : { background: "#fff", borderColor: "var(--border)" }}>
                {s.libelle}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {/* 3a. Oui : s'inscrire tout de suite */}
      {d.suite === "oui" && (
        <section className="card-flat text-center space-y-3" data-testid="avis-oui" ref={refApres}>
          <div className="text-[17px] font-semibold">Génial, on est ravis ! ✨</div>
          <p className="text-[15px] text-[var(--muted)]">{qui ? `Inscris ${qui}` : "Inscris-toi"} au cours en deux minutes, la place est gardée pour toi.</p>
          <a href={d.lien_inscription} className="btn-primary w-full justify-center text-[16px]" data-testid="avis-inscrire">
            Je m'inscris{d.cours_nom ? ` au cours ${d.cours_nom}` : ""}
          </a>
        </section>
      )}

      {/* 3b. J'hésite / Non : pourquoi, en un toucher */}
      {(d.suite === "hesite" || d.suite === "non") && (
        <section className="card-flat space-y-4" data-testid="avis-raisons" ref={refApres}>
          <div className="text-[17px] font-semibold text-center">{d.suite === "hesite" ? "Qu'est-ce qui te fait hésiter ?" : "Qu'est-ce qui n'a pas convenu ?"}</div>
          <div className="flex flex-wrap justify-center gap-2">
            {d.raisons_possibles.map((r) => (
              <button key={r.cle} type="button" onClick={() => basculer(r.cle)} aria-pressed={raisons.has(r.cle)} data-testid={`avis-raison-${r.cle}`}
                className="rounded-full border px-4 py-2 text-[14px] font-medium transition-colors"
                style={raisons.has(r.cle) ? { background: "#0F1B2D", borderColor: "#0F1B2D", color: "#fff" } : { background: "#fff", borderColor: "var(--border)" }}>
                {raisons.has(r.cle) && <Check size={13} className="inline -mt-0.5 mr-1" />}{r.libelle}
              </button>
            ))}
          </div>
          {raisons.has("autre") && (
            <input className="input-field w-full" placeholder="Dis-nous en un mot…" value={autre} maxLength={300}
              onChange={(e) => setAutre(e.target.value)} onBlur={() => autre !== (d.autre || "") && envoyer({ autre })} data-testid="avis-autre" />
          )}
          {/* Un raccourci utile selon la raison. */}
          <div className="grid gap-2">
            {(raisons.has("autre_cours") || raisons.has("prof")) && <a className="btn-primary justify-center" href={d.lien_essai} data-testid="avis-autre-essai">Réserver un autre cours d'essai</a>}
            {raisons.has("horaire") && <a className="btn-ghost justify-center" href={d.lien_planning}>Voir tous les horaires</a>}
            {raisons.has("tarif") && <a className="btn-ghost justify-center" href={d.lien_tarifs}>Voir les tarifs (rabais famille, carte 10 cours)</a>}
          </div>
          <button type="button" onClick={() => envoyer({ rappel: !d.rappel })} data-testid="avis-rappel"
            className="w-full rounded-2xl border px-4 py-3 text-[15px] flex items-center justify-center gap-2"
            style={d.rappel ? { background: "#F0FDF4", borderColor: "#86EFAC", color: "#15803D" } : { background: "#fff", borderColor: "var(--border)" }}>
            {d.rappel ? <Check size={16} /> : <Phone size={16} />} {d.rappel ? "L'école te rappelle" : "Je veux bien qu'on me rappelle"}
          </button>
        </section>
      )}

      {/* 4. Un mot (facultatif), puis Terminer */}
      {d.suite && (
        <section className="card-flat space-y-3" data-testid="avis-fin">
          {fini ? (
            <div className="text-center py-2" data-testid="avis-merci">
              <div className="text-[19px] font-semibold">Merci, c'est noté ! 💛</div>
              <p className="text-[14px] text-[var(--muted)] mt-1">{d.rappel ? "L'école te rappelle très vite." : "À bientôt au studio !"}</p>
              <a href="/" className="inline-block mt-3 text-[14px] font-semibold text-[var(--primary)]" data-testid="avis-retour-site">Retour au site</a>
            </div>
          ) : (
            <>
              <textarea className="input-field w-full" rows={2} maxLength={1000} placeholder="Un mot pour l'école ? (facultatif)"
                value={mot} onChange={(e) => setMot(e.target.value)} data-testid="avis-mot" />
              <button type="button" className="btn-primary w-full justify-center" disabled={envoi} data-testid="avis-terminer"
                onClick={() => envoyer({ termine: true, ...(mot !== (d.commentaire || "") ? { commentaire: mot } : {}), ...(raisons.has("autre") && autre !== (d.autre || "") ? { autre } : {}) })}>
                {envoi ? <Loader2 size={16} className="animate-spin" /> : "Envoyer"}
              </button>
            </>
          )}
        </section>
      )}
      {erreur && <p className="text-center text-sm" style={{ color: "#B91C1C" }}>{erreur}</p>}
      <p className="text-center text-xs text-[var(--muted)]">Chaque réponse est enregistrée tout de suite.</p>
    </Cadre>
  );
}

const Cadre = ({ children }) => (
  <div data-testid="page-avis-essai" className="pt-28 pb-24 relative overflow-hidden mesh-bg">
    <PageDecor />
    <div className="max-w-md mx-auto px-5 relative z-10 space-y-4">{children}</div>
  </div>
);
